import { useState, useEffect } from 'react'
/*
  This hook is responsible for checking the existing H2 and H3 tags on the Blog Post.
  They are translated to Table of Contents. Since the current Markup cannot include id's,
  they are added dynamically to the appropriate titles, to serve as Anchors.
*/

export function titleToAnchor(title: string) {
  return title
    .replace(/[^a-zA-Z ]/g, '')
    .trim()
    .toLowerCase()
    .split(' ')
    .join('_')
}

function getHeadings() {
  const mainContent = document.querySelector('[class*="mainContent"]')
  if (!mainContent) return []
  const headings = Array.from(
    mainContent.querySelectorAll<HTMLHeadingElement>('h2, h3'),
  )

  headings.forEach((heading) => {
    let anchor = titleToAnchor(heading.innerText)
    // Make sure there are no duplicates
    let i = 1
    while (headings.find((h) => h.id === anchor)) {
      anchor = anchor + '_' + i
      i++
    }
    heading.setAttribute('id', anchor)
  })

  return headings
}

const useHeadings = () => {
  const [headings, setHeadings] = useState<HTMLHeadingElement[]>()
  const [activeID, setActiveID] = useState<string>()

  useEffect(() => {
    const headings = getHeadings()
    setHeadings(headings)

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            entry.boundingClientRect.bottom > (entry.rootBounds?.bottom ?? 0)
          ) {
            // Intersects at the bottom (scroll down)
            if (entry.isIntersecting) setActiveID(entry.target.id)
            // Stops intersecting at the bottom (scroll up)
            else {
              const last = headings.findIndex((h) => h === entry.target)
              setActiveID(last > 0 ? headings[last - 1].id : undefined)
            }
          } else if (entry.isIntersecting) {
            // Intersects at top, mostly when clicking an anchor link
            setActiveID(entry.target.id)
            break
          }
        }
      },
      { rootMargin: '0% 0% -60% 0%' },
    )
    headings.forEach((heading) => {
      observer.observe(heading)
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  return { headings, activeID }
}

export default useHeadings
